import { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export interface OccurrenceWaiveConfirmProps {
  /** `waive` collects a mandatory reason; `restore` clears an existing waive; `null` = closed. */
  mode: "waive" | "restore" | null;
  /** The occurrence being acted on — named in the prompt. */
  date: Date;
  isPending: boolean;
  /** Reason is passed for `waive`, omitted for `restore`. */
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}

/**
 * Lean confirm dialog for waiving / restoring the charge on a SINGLE occurrence, shared by the
 * Forthcoming and Past occurrence lists. Matches the Exclude/Reinstate idiom: no scope choice
 * (the row is the scope). The reason is mandatory when waiving (the backend enforces it too) and
 * absent when restoring. Owns no mutation — the caller wires it up and controls `mode`.
 */
export const OccurrenceWaiveConfirm = ({ mode, date, isPending, onConfirm, onClose }: OccurrenceWaiveConfirmProps) => {
  const [reason, setReason] = useState("");

  // Reset the reason whenever the target occurrence (or the mode) changes, so a reason typed for
  // one date never carries over to the next row the operator opens.
  useEffect(() => {
    setReason("");
  }, [date.getTime(), mode]);

  const isWaive = mode === "waive";
  const reasonMissing = isWaive && !reason.trim();

  return (
    <AlertDialog
      open={mode !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <AlertDialogContent data-testid="occurrence-waive-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{isWaive ? "Waive this occurrence's fee?" : "Restore this charge?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {isWaive
              ? `The fee for ${format(date, "EEEE d MMMM yyyy")} will be waived — this occurrence will not be billed. The change is logged in the activity trail.`
              : `The fee for ${format(date, "EEEE d MMMM yyyy")} will be charged again. The change is logged in the activity trail.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isWaive && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="occurrence-waive-reason" className="text-sm font-medium">
              Reason for waiving <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="occurrence-waive-reason"
              data-testid="occurrence-waive-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this occurrence free of charge? (e.g. comped due to a room double-booking)"
              aria-required
              disabled={isPending}
              className="h-20 bg-white"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => (isWaive ? onConfirm(reason.trim()) : onConfirm())}
            disabled={isPending || reasonMissing}
            data-testid="occurrence-waive-confirm-action"
          >
            {isPending ? "Saving…" : isWaive ? "Waive fee" : "Restore charge"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default OccurrenceWaiveConfirm;
